import React, { useState, useEffect, useRef } from 'react';
import { Send, X, Minimize2, MessageCircle, User, CheckCheck, Clock, Wifi, WifiOff, Edit2 } from 'lucide-react';
import io from 'socket.io-client';
import { useChat } from '../../components/chatcontext/ChatContext';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:5000';

const CustomerChat = () => {
  const { isCustomerChatOpen, closeCustomerChat } = useChat();
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [userName, setUserName] = useState('Guest');
  const [isEditingName, setIsEditingName] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [adminOnline, setAdminOnline] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connecting');
  
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const userIdRef = useRef(null);

  // Auto-scroll ke bawah
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load saved data on mount
  useEffect(() => {
    const savedName = localStorage.getItem('chatUserName');
    const savedUserId = localStorage.getItem('chatUserId');
    if (savedName) setUserName(savedName);
    userIdRef.current = savedUserId || `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('chatUserId', userIdRef.current);
  }, []);

  // LAZY CONNECT: Only connect socket when chat is opened
  useEffect(() => {
    if (!isCustomerChatOpen) {
      // Disconnect when chat is closed
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
        setConnectionStatus('disconnected');
      }
      return;
    }

    // Connect to socket only when chat opens
    socketRef.current = io(SOCKET_URL, {
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
      timeout: 20000,
      autoConnect: true
    });

    socketRef.current.on('connect', () => {
      setIsConnected(true);
      setConnectionStatus('connected');
      socketRef.current.emit('join', {
        userId: userIdRef.current,
        userType: 'customer',
        userName: userName
      });
    });

    socketRef.current.on('disconnect', () => {
      setIsConnected(false);
      setConnectionStatus('disconnected');
    });

    socketRef.current.on('reconnect_attempt', () => {
      setConnectionStatus('reconnecting');
    });

    socketRef.current.on('reconnect', () => {
      setConnectionStatus('connected');
      setIsConnected(true);
    });

    socketRef.current.on('admin_status', (data) => {
      setAdminOnline(data.online);
    });

    socketRef.current.on('chat_history', (history) => {
      const formattedMessages = history.map(msg => ({
        id: msg.id, text: msg.text, sender: msg.sender,
        senderName: msg.senderName, timestamp: msg.timestamp,
        status: msg.status || 'delivered', isGuest: msg.isGuest
      }));
      setMessages(formattedMessages);
    });

    socketRef.current.on('new_message', (msg) => {
      setMessages(prev => [...prev, {
        id: msg.id, text: msg.text, sender: msg.sender,
        senderName: msg.senderName, timestamp: msg.timestamp,
        status: msg.status || 'delivered'
      }]);
      setIsTyping(false);
      if (isMinimized) setUnreadCount(prev => prev + 1);
    });

    socketRef.current.on('message_sent', (msg) => {
      setMessages(prev => prev.map(m =>
        m.id === 'temp_' + msg.tempId ? { ...msg, status: msg.status || 'delivered' } : m
      ));
    });

    socketRef.current.on('message_failed', (tempId) => {
      setMessages(prev => prev.map(m =>
        m.id === 'temp_' + tempId ? { ...m, status: 'failed' } : m
      ));
    });

    socketRef.current.on('admin_typing', () => setIsTyping(true));
    socketRef.current.on('admin_stop_typing', () => setIsTyping(false));

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [isCustomerChatOpen]);

  // Update chat history ketika nama berubah
  useEffect(() => {
    if (isConnected && socketRef.current) {
      socketRef.current.emit('join', {
        userId: userIdRef.current,
        userType: 'customer',
        userName: userName
      });
    }
  }, [userName, isConnected]);

  // Kirim pesan
  const handleSendMessage = () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || !socketRef.current?.connected) return;

    // Generate temp ID untuk tracking
    const tempId = Date.now();

    // Buat temporary message dengan status 'delivered'
    const tempMessage = {
      id: `temp_${tempId}`,
      text: trimmedMessage,
      sender: 'customer',
      senderName: userName,
      timestamp: new Date().toISOString(),
      status: 'delivered'
    };

    setMessages(prev => [...prev, tempMessage]);
    
    // Kirim ke server dengan tempId untuk konfirmasi
    socketRef.current.emit('send_message', {
      text: trimmedMessage,
      recipientId: null,
      tempId: tempId
    });

    setMessage('');
    handleStopTyping();
    scrollToBottom();
  };

  // Handle typing indicator
  const handleTyping = () => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('typing', { userType: 'customer' });
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        handleStopTyping();
      }, 3000);
    }
  };

  const handleStopTyping = () => {
    if (socketRef.current?.connected) {
      socketRef.current.emit('stop_typing', { userType: 'customer' });
    }
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Update nama user
  const handleUpdateName = () => {
    const trimmedName = userName.trim();
    if (!trimmedName) {
      alert('Nama tidak boleh kosong');
      return;
    }

    setUserName(trimmedName);
    localStorage.setItem('chatUserName', trimmedName);
    setIsEditingName(false);
    
    // Re-join dengan nama baru
    if (socketRef.current?.connected) {
      socketRef.current.emit('join', {
        userId: userIdRef.current,
        userType: 'customer',
        userName: trimmedName
      });
    }
  };

  // Minimize chat
  const minimizeChat = () => {
    setIsMinimized(!isMinimized);
  };

  // Format waktu
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('id-ID', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Format tanggal untuk header
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('id-ID', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get connection status text
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Terhubung';
      case 'connecting': return 'Menghubungkan...';
      case 'reconnecting': return 'Menyambung ulang...';
      case 'disconnected': return 'Terputus';
      case 'error': return 'Koneksi Error';
      default: return 'Menghubungkan...';
    }
  };

  // Get status color
  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-green-100';
      case 'connecting': return 'text-yellow-100';
      case 'reconnecting': return 'text-yellow-100';
      default: return 'text-red-200';
    }
  };

  return (
    <div className="fixed bottom-16 right-4 z-50">
      {/* Chat Window */}
      {isCustomerChatOpen && (
        <div className={`bg-white rounded-lg shadow-2xl w-96 max-w-[calc(100vw-2rem)] transition-all duration-300 ${
          isMinimized ? 'h-14' : 'h-[600px]'
        }`}>
          {/* Header */}
          <div className="bg-green-600 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <MessageCircle className="text-green-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Customer Service</h3>
                <div className="flex items-center space-x-2 text-xs">
                  {isConnected ? (
                    <Wifi size={12} className="text-green-100" />
                  ) : (
                    <WifiOff size={12} className="text-red-200" />
                  )}
                  <span className={getStatusColor()}>
                    {getConnectionStatusText()} • {adminOnline ? 'Admin Online' : 'Admin Offline'}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={minimizeChat}
                className="hover:bg-green-700 p-1 rounded transition-colors"
                title={isMinimized ? "Maximize" : "Minimize"}
              >
                <Minimize2 size={18} />
              </button>
              <button
                onClick={closeCustomerChat}
                className="hover:bg-green-700 p-1 rounded transition-colors"
                title="Tutup"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {!isMinimized && (
            <>
              {/* Messages Area */}
              <div className="h-[calc(600px-140px)] overflow-y-auto p-4 space-y-4 bg-gray-50">
                {!isConnected && connectionStatus === 'error' && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded text-sm">
                    ❌ Gagal terhubung ke server. Silakan refresh halaman.
                  </div>
                )}
                
                {connectionStatus === 'reconnecting' && (
                  <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded text-sm">
                    🔄 Menyambung ulang...
                  </div>
                )}

                {/* Welcome Message */}
                {messages.length === 0 && isConnected && (
                  <div className="text-center text-gray-500 py-8">
                    <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="font-semibold mb-2">Selamat Datang! 👋</p>
                    <p className="text-sm mb-1">Halo {userName}, ada yang bisa kami bantu?</p>
                    <p className="text-xs">Chat langsung dengan customer service kami.</p>
                  </div>
                )}
                
                {/* Messages List */}
                {messages.map((msg, index) => {
                  const showDateHeader = index === 0 || 
                    new Date(msg.timestamp).toDateString() !== 
                    new Date(messages[index - 1].timestamp).toDateString();
                  
                  return (
                    <div key={msg.id}>
                      {/* Date Header */}
                      {showDateHeader && (
                        <div className="text-center my-4">
                          <span className="bg-gray-200 text-gray-600 text-xs px-3 py-1 rounded-full">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                      )}
                      
                      {/* Message */}
                      <div className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[75%] rounded-lg p-3 ${
                            msg.sender === 'customer'
                              ? 'bg-green-600 text-white rounded-br-none'
                              : 'bg-white text-gray-800 rounded-bl-none shadow'
                          }`}
                        >
                          {msg.sender === 'admin' && msg.senderName && (
                            <div className="text-xs text-gray-500 mb-1 font-medium">
                              {msg.senderName}
                            </div>
                          )}
                          <p className="text-sm break-words whitespace-pre-wrap">{msg.text}</p>
                          <div className="flex items-center justify-end space-x-1 mt-1">
                            <span className={`text-xs ${
                              msg.sender === 'customer' ? 'text-green-100' : 'text-gray-500'
                            }`}>
                              {formatTime(msg.timestamp)}
                            </span>
                            {msg.sender === 'customer' && (
                              <span>
                                {msg.status === 'sending' && (
                                  <Clock size={12} className="text-green-100" />
                                )}
                                {(msg.status === 'sent' || msg.status === 'delivered') && (
                                  <CheckCheck size={12} className="text-green-100" />
                                )}
                                {msg.status === 'failed' && (
                                  <span className="text-red-300 text-xs ml-1">!</span>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-lg p-3 max-w-[75%] rounded-bl-none shadow">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-gray-200 bg-white">
                {/* Name Editor */}
                <div className="flex items-center space-x-2 mb-3">
                  <span className="text-xs text-gray-600 flex items-center">
                    <User size={12} className="mr-1" />
                    Nama:
                  </span>
                  {isEditingName ? (
                    <div className="flex items-center space-x-2 flex-1">
                      <input
                        type="text"
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleUpdateName();
                          if (e.key === 'Escape') setIsEditingName(false);
                        }}
                        placeholder="Masukkan nama Anda"
                        className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-green-500"
                        autoFocus
                      />
                      <button
                        onClick={handleUpdateName}
                        className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-xs"
                      >
                        Simpan
                      </button>
                      <button
                        onClick={() => setIsEditingName(false)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-2 py-1 rounded text-xs"
                      >
                        Batal
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2 flex-1">
                      <span className="text-sm font-medium flex-1">{userName}</span>
                      <button
                        onClick={() => setIsEditingName(true)}
                        className="text-gray-500 hover:text-gray-700"
                        title="Edit nama"
                      >
                        <Edit2 size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={message}
                    onChange={(e) => {
                      setMessage(e.target.value);
                      handleTyping();
                    }}
                    onKeyDown={handleKeyPress}
                    placeholder={isConnected ? "Ketik pesan Anda..." : "Menghubungkan..."}
                    disabled={!isConnected}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    maxLength={1000}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!message.trim() || !isConnected}
                    className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Kirim pesan"
                  >
                    <Send size={20} />
                  </button>
                </div>
                
                {/* Info Footer */}
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">
                    {message.length}/1000 karakter
                  </p>
                  <p className="text-xs text-gray-500">
                    💬 Chat langsung • Tekan Enter untuk kirim
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerChat;